import {ZadIssuerProfile, ZadIssuerProfileWritePayload} from '../../../models/zad'
import {getFilePath} from '../../../helpers/file-helpers'
import {isZadManagementRole} from './reportHelpers'
import {resolveZadUserMessage, ZadApiError} from './errors'

export const ZAD_ISSUER_LOGO_MAX_BYTES = 2 * 1024 * 1024
export const ZAD_ISSUER_LOGO_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const
export const ZAD_ISSUER_LOGO_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.webp'] as const

export const ZAD_ISSUER_WRITE_FIELDS: Array<keyof ZadIssuerProfileWritePayload> = [
  'commercialName',
  'arabicName',
  'legalName',
  'address',
  'city',
  'postalCode',
  'country',
  'taxId',
  'registrationNumber',
  'phone',
  'secondaryPhone',
  'email',
  'bankName',
  'bankAccount',
  'iban',
  'rib',
]

export function canEditZadIssuerProfile(role?: string | number | null): boolean {
  return isZadManagementRole(role)
}

export function emptyZadIssuerFormValues(): ZadIssuerProfileWritePayload {
  return {
    commercialName: '',
    arabicName: '',
    legalName: '',
    address: '',
    city: '',
    postalCode: '',
    country: '',
    taxId: '',
    registrationNumber: '',
    phone: '',
    secondaryPhone: '',
    email: '',
    bankName: '',
    bankAccount: '',
    iban: '',
    rib: '',
  }
}

function asIssuerString(value: unknown): string {
  if (value == null) return ''
  return String(value)
}

export function toZadIssuerFormValues(
  profile?: ZadIssuerProfile | null
): ZadIssuerProfileWritePayload {
  const empty = emptyZadIssuerFormValues()
  if (!profile) return empty
  return {
    commercialName: asIssuerString(profile.commercialName),
    arabicName: asIssuerString(profile.arabicName),
    legalName: asIssuerString(profile.legalName),
    address: asIssuerString(profile.address),
    city: asIssuerString(profile.city),
    postalCode: asIssuerString(profile.postalCode),
    country: asIssuerString(profile.country),
    taxId: asIssuerString(profile.taxId),
    registrationNumber: asIssuerString(profile.registrationNumber),
    phone: asIssuerString(profile.phone),
    secondaryPhone: asIssuerString(profile.secondaryPhone),
    email: asIssuerString(profile.email),
    bankName: asIssuerString(profile.bankName),
    bankAccount: asIssuerString(profile.bankAccount),
    iban: asIssuerString(profile.iban),
    rib: asIssuerString(profile.rib),
  }
}

export function sanitizeZadIssuerWritePayload(
  values: ZadIssuerProfileWritePayload
): ZadIssuerProfileWritePayload {
  const body: ZadIssuerProfileWritePayload = emptyZadIssuerFormValues()
  ZAD_ISSUER_WRITE_FIELDS.forEach((field) => {
    body[field] = asIssuerString(values[field])
  })
  return body
}

export function extractZadIssuerProfile(data: unknown): ZadIssuerProfile | null {
  if (!data || typeof data !== 'object') return null
  const raw = data as Record<string, unknown>
  const nested = raw.issuerProfile || raw.profile || raw.issuer
  if (nested && typeof nested === 'object') return nested as ZadIssuerProfile
  if (raw.commercialName != null || raw.arabicName != null || raw.logoUrl != null) {
    return raw as ZadIssuerProfile
  }
  return null
}

export function resolveZadIssuerLogoUrl(logoUrl?: string | null): string {
  return getFilePath(logoUrl)
}

export function isZadIssuerEmailValid(email: string): boolean {
  const value = String(email || '').trim()
  if (!value) return true
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

export function validateZadIssuerForm(values: ZadIssuerProfileWritePayload): string | null {
  if (!isZadIssuerEmailValid(values.email)) return 'ZAD.SETTINGS.EMAIL_INVALID'
  return null
}

function logoExtensionAllowed(fileName: string): boolean {
  const name = String(fileName || '').toLowerCase()
  return ZAD_ISSUER_LOGO_EXTENSIONS.some((ext) => name.endsWith(ext))
}

export function validateZadIssuerLogoFile(file?: File | null): string | null {
  if (!file) return 'ZAD.SETTINGS.LOGO_REQUIRED'
  const typeOk =
    ZAD_ISSUER_LOGO_MIME_TYPES.includes(file.type as (typeof ZAD_ISSUER_LOGO_MIME_TYPES)[number]) ||
    (!file.type && logoExtensionAllowed(file.name))
  if (!typeOk) return 'ZAD.SETTINGS.LOGO_TYPE_INVALID'
  if (file.size > ZAD_ISSUER_LOGO_MAX_BYTES) return 'ZAD.SETTINGS.LOGO_TOO_LARGE'
  return null
}

export function resolveZadIssuerMessage(err: ZadApiError): {i18nId: string; fallback: string} {
  const map: Record<string, string> = {
    ROLE_FORBIDDEN: 'ZAD.COMMON.FORBIDDEN',
    VALIDATION_ERROR: 'ZAD.SETTINGS.VALIDATION_ERROR',
    INVALID_FILE_TYPE: 'ZAD.SETTINGS.LOGO_TYPE_INVALID',
    FILE_TOO_LARGE: 'ZAD.SETTINGS.LOGO_TOO_LARGE',
    UPLOAD_FAILED: 'ZAD.SETTINGS.LOGO_UPLOAD_FAILED',
  }
  if (err.errorCode && map[err.errorCode]) {
    return {i18nId: map[err.errorCode], fallback: err.error}
  }
  return resolveZadUserMessage(err)
}
